// Shared SEO helpers used across book links & detail pages so the URL
// always matches the sitemap: /books/{title}-{author}-{isbn|id}
//
// Backend has an identical `_book_seo_slug` implementation so the two stay
// aligned. If you change the pattern here, mirror it in backend/server.py.

export function slugify(value) {
  if (!value) return "book";
  return String(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "book";
}

export function bookSeoSlug(book) {
  if (!book) return "book";
  const title = slugify(book.title);
  const author = book.author ? slugify(book.author) : "";
  const isbn = (book.isbn || "").replace(/[^A-Za-z0-9]/g, "");
  const tail = isbn || book.id || "";
  return [title, author, tail].filter(Boolean).join("-").slice(0, 120);
}

export function bookUrl(book) {
  // Prefer server-provided slug (guaranteed to match sitemap), fall back to
  // a client-side build so links never break if `slug` isn't returned yet.
  const slug = (book && book.slug) || bookSeoSlug(book);
  return `/books/${slug}`;
}

// ---- Canonical + title helpers ----
// Every canonical URL on the site MUST point to https://www.gainabooks.com so
// Google/Bing consolidate ranking signals on one hostname.
export const CANONICAL_ORIGIN = "https://www.gainabooks.com";

export function _upsertLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function _upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * Apply title, meta description and canonical link for a page in one call.
 * Every canonical is forced to https://www.gainabooks.com so alias hosts
 * (preview, apex) don't split ranking.
 */
export function applyPageSeo({ title, description, path, keywords, ogImage }) {
  if (title) document.title = title;
  if (description) _upsertMeta("name", "description", description);
  if (keywords) _upsertMeta("name", "keywords", keywords);
  const canonical = `${CANONICAL_ORIGIN}${path || "/"}`;
  _upsertLink("canonical", canonical);
  if (title) _upsertMeta("property", "og:title", title);
  if (description) _upsertMeta("property", "og:description", description);
  _upsertMeta("property", "og:url", canonical);
  if (ogImage) _upsertMeta("property", "og:image", ogImage);
}

